import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { typeDef } from '../featureTypes/registry';
import { geometryAnchor } from '../geo/measure';
import type { FeatureRecord, Status } from '../lib/types';
import { syncStateOf } from '../lib/types';
import { featureLink } from '../lib/urlState';
import { flyTo, isAdmin, isEditor, setState, toast, useStore } from '../store/store';
import {
  deleteFeature,
  onConflict,
  reapplyPatch,
  reloadFeature,
  setStatus,
  type PendingConflict,
} from '../store/actions';
import { Icon } from './Icon';
import { PropertiesTab } from './inspector/PropertiesTab';
import { GeometryTab } from './inspector/GeometryTab';
import { UnityTab } from './inspector/UnityTab';
import { CommentsTab } from './inspector/CommentsTab';
import { HistoryTab } from './inspector/HistoryTab';

type Tab = 'properties' | 'geometry' | 'unity' | 'comments' | 'history';

const EDITOR_NEXT: Partial<Record<Status, Status[]>> = { idea: ['proposed'], proposed: ['idea'] };
const ADMIN_PRIMARY: Partial<Record<Status, Status>> = {
  idea: 'proposed',
  proposed: 'approved',
  approved: 'in_unity',
  needs_update: 'in_unity',
};

export function Inspector() {
  const { t } = useTranslation();
  const f = useStore((s) => (s.selectedId ? s.features[s.selectedId] : undefined));
  const user = useStore((s) => s.user);
  const users = useStore((s) => s.users);
  const editingShapeId = useStore((s) => s.editingShapeId);
  const uo = useStore((s) => (f?.unityKey ? s.unityObjects[f.unityKey] : undefined));
  const [tab, setTab] = useState<Tab>('properties');
  const [conflict, setConflict] = useState<PendingConflict | null>(null);

  useEffect(() => {
    onConflict(setConflict);
    return () => onConflict(() => undefined);
  }, []);

  if (!f) return null;
  const def = typeDef(f.type);
  const editor = isEditor(user);
  const admin = isAdmin(user);
  const linked = !!f.unityKey || f.status === 'in_unity' || f.status === 'needs_update';
  const sync = syncStateOf(f, uo);
  // Gone from Unity (deleted there): an admin can drop it instead of just deprecating it.
  const gone = admin && sync === 'missing_in_unity';
  const canDelete = admin
    ? !linked || gone
    : editor && f.createdBy === user?.id && (f.status === 'idea' || f.status === 'proposed');
  const canEditShape = editor && f.geometry.type !== 'none';

  const nextStatuses: Status[] = admin
    ? (['idea', 'proposed', 'approved', 'in_unity', 'needs_update', 'deprecated'] as Status[]).filter(
        (s) => s !== f.status,
      )
    : editor
      ? (EDITOR_NEXT[f.status] ?? [])
      : [];
  const primary = admin ? ADMIN_PRIMARY[f.status] : EDITOR_NEXT[f.status]?.[0];

  const zoomTo = () => {
    const a = geometryAnchor(f.geometry);
    if (a) flyTo(a.x, a.z);
  };
  const copyLink = () => {
    void navigator.clipboard?.writeText(featureLink(f.key)).then(() => toast(t('common.copied')));
  };

  return (
    <aside className="inspector">
      <header className="insp-head">
        <span className="swatch" style={{ ['--c' as string]: f.color || def.color }}>
          <Icon id={def.icon} size={14} />
        </span>
        <div className="insp-title">
          <div className="name">{f.name}</div>
          <div className="muted small mono ellipsis" title={f.key}>
            {t(`types.${f.type}`)} · {f.key}
          </div>
        </div>
        <button
          className="ghost icon-only"
          onClick={() => setState({ selectedId: null, editingShapeId: null })}
          title={t('common.close')}
        >
          <Icon id="close" />
        </button>
      </header>

      {conflict && conflict.featureId === f.id && (
        <div className="banner warn">
          <div>{t('inspector.conflict', { name: users[f.updatedBy]?.name ?? t('common.someone') })}</div>
          <div className="row">
            <button onClick={() => void reloadFeature(f.id)}>{t('inspector.reloadTheirs')}</button>
            <button className="primary" onClick={() => void reapplyPatch(conflict)}>
              {t('inspector.applyMine')}
            </button>
          </div>
        </div>
      )}

      <div className="insp-status">
        <span className={`status-pill ${f.status}`}>{t(`status.${f.status}`)}</span>
        {f.unityKey && <span className={`sync-pill ${sync}`}>{t(`sync.${sync}`)}</span>}
        <div className="spacer" />
        {primary && nextStatuses.includes(primary) && (
          <button className="primary small" onClick={() => void setStatus(f.id, primary)}>
            {t(`inspector.to.${primary}`)}
          </button>
        )}
        {nextStatuses.length > 1 && (
          <select
            className="small"
            value=""
            onChange={(e) => e.target.value && void setStatus(f.id, e.target.value as Status)}
            aria-label={t('inspector.setStatus')}
          >
            <option value="">{t('inspector.setStatus')}</option>
            {nextStatuses.map((s) => (
              <option key={s} value={s}>
                {t(`status.${s}`)}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="insp-actions">
        <button onClick={zoomTo} disabled={f.geometry.type === 'none'}>
          <Icon id="focus" /> {t('inspector.zoomTo')}
        </button>
        {canEditShape && (
          <button
            className={editingShapeId === f.id ? 'active' : ''}
            onClick={() =>
              setState({ editingShapeId: editingShapeId === f.id ? null : f.id, tool: { kind: 'none' } })
            }
          >
            <Icon id="tool-vertices" />{' '}
            {editingShapeId === f.id ? t('inspector.doneEditing') : t('inspector.editShape')}
          </button>
        )}
        <button onClick={copyLink}>
          <Icon id="tool-copy" /> {t('inspector.copyLink')}
        </button>
        {canDelete && (
          <button
            className="danger"
            title={gone ? t('inspector.removeGone') : undefined}
            onClick={() => {
              const msg = gone
                ? t('inspector.confirmRemoveGone', { name: f.name })
                : t('inspector.confirmDelete', { name: f.name });
              if (confirm(msg)) void deleteFeature(f.id);
            }}
          >
            <Icon id="trash" />
          </button>
        )}
      </div>

      <nav className="tabs">
        {(['properties', 'geometry', 'unity', 'comments', 'history'] as Tab[]).map((k) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {t(`inspector.tabs.${k}`)}
          </button>
        ))}
      </nav>
      <div className="tab-body">
        {tab === 'properties' && <PropertiesTab key={f.id} feature={f} />}
        {tab === 'geometry' && <GeometryTab key={f.id} feature={f} />}
        {tab === 'unity' && <UnityTab feature={f} />}
        {tab === 'comments' && <CommentsTab key={f.id} feature={f} />}
        {tab === 'history' && <HistoryTab key={f.id} feature={f} />}
      </div>
      <Footer f={f} />
    </aside>
  );
}

function Footer({ f }: { f: FeatureRecord }) {
  const { t } = useTranslation();
  const users = useStore((s) => s.users);
  return (
    <footer className="insp-foot muted small">
      {t('inspector.createdBy', { name: users[f.createdBy]?.name ?? '—' })} ·{' '}
      {t('inspector.updatedBy', { name: users[f.updatedBy]?.name ?? '—' })} · rev {f.rev}
    </footer>
  );
}
